/**
 * Hide and Seek Teams: a warning for the admin when the teams have hidden in
 * different numbers of rounds, so one has had more chances to score. Counts
 * the rounds the standings count, plus a round still in play (ending the game
 * ends and counts it).
 */

import {
  GameType,
  HNST_TEAM_KEYS,
  HNST_TEAMS,
  isHnstSettings,
  isRoundActive,
  type Game,
  type HnstTeamKey,
} from '@swtorbit/shared-socket-events';

export function hnstUnevenHidingWarning(game: Game): string | null {
  if (game.gameType !== GameType.HIDE_AND_SEEK_TEAMS) return null;

  const hidden = new Map<HnstTeamKey, number>(HNST_TEAM_KEYS.map((team) => [team, 0]));
  for (const standing of game.hnstStandings?.teams ?? []) hidden.set(standing.team, standing.roundsHidden);
  const inPlay = game.rounds.find((round) => isRoundActive(round));
  if (inPlay && !inPlay.disabled && isHnstSettings(inPlay.settings) && inPlay.settings.hidingTeam) {
    hidden.set(inPlay.settings.hidingTeam, (hidden.get(inPlay.settings.hidingTeam) ?? 0) + 1);
  }

  const [a, b] = HNST_TEAM_KEYS.map((team) => ({ team, rounds: hidden.get(team) ?? 0 }));
  if (a.rounds === b.rounds) return null;
  const [more, fewer] = a.rounds > b.rounds ? [a, b] : [b, a];
  const rounds = (n: number) => `${n} round${n === 1 ? '' : 's'}`;
  return `${HNST_TEAMS[more.team].name} have hidden ${rounds(more.rounds)}, ${HNST_TEAMS[fewer.team].name} ${rounds(fewer.rounds)}: `
    + `play ${more.rounds - fewer.rounds === 1 ? 'another round' : 'more rounds'} with ${HNST_TEAMS[fewer.team].name} hiding so both teams hide equally.`;
}
